/* Compact media picker — replaces the old "Choose content" modal for bundles,
   paid-plan instruction photos and per-item preview media. A vault rail on the
   left (search + "Selected" view), a grid with numbered checks in pick order, and
   a footer tray with a summary. Clicking the backdrop does nothing; Cancel, ×
   and Esc throw the draft away. */

import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useSalesCatalogue, type CatalogueItem, type SalesCatalogue } from '../SalesLadder/useSalesCatalogue';
import { isVideoItem, fmtDuration } from '../SalesLadder/MediaBadge';
import { VaultThumb } from '../Sales/VaultThumb';
import { Btn, Icon, Seg, Spin } from './kit';
import { MediaLightbox } from './MediaLightbox';
import { useResolveIds } from './resolveIds';
import { font } from './style';
import { C } from './tokens';

export interface PickFit { text: string; tone: 'ok' | 'warn' }

interface Props {
  accountId: string;
  title: string;
  initialSelected: string[];
  /** content_id → vault_id already known for the selection (e.g. a rung's content_vaults). */
  vaultHints?: Record<string, string>;
  /** The caller's catalogue, when it has one: media it already loaded (e.g. the
   *  ladder rows' thumbnails) shows at once instead of being fetched again. */
  catalogue?: SalesCatalogue;
  /** Ids hidden from the grid (an item can't preview itself). */
  excludeIds?: string[];
  /** Start on a type filter (instruction photos open on Photos). */
  initialType?: 'all' | 'photo' | 'video';
  /** Footer hint under the summary, given the current mix. */
  fit?: (photos: number, videos: number) => PickFit | null;
  /** Allow confirming an empty selection (clears the field). */
  allowEmpty?: boolean;
  onConfirm: (ids: string[], items: CatalogueItem[]) => void;
  onClose: () => void;
}

type Sort = 'recent' | 'oldest' | 'longest';

const thumbUrl = (it: CatalogueItem) => it.thumb_url || it.preview_url || (isVideoItem(it.type) ? null : it.full_url);

export function MediaPicker(props: Props) {
  return props.catalogue ? <Picker {...props} cat={props.catalogue} /> : <OwnCataloguePicker {...props} />;
}

function OwnCataloguePicker(props: Props) {
  return <Picker {...props} cat={useSalesCatalogue(props.accountId)} />;
}

function Picker({ accountId, title, initialSelected, vaultHints, excludeIds, initialType = 'all', fit, allowEmpty, onConfirm, onClose, cat }: Props & { cat: SalesCatalogue }) {
  const { t } = useTranslation('assistantCompact');
  const [sel, setSel] = useState<string[]>(initialSelected);
  const [vault, setVault] = useState<string>('');
  // Open on what's already picked: those load first, and no vault is browsed
  // (fetched) until the operator asks for one.
  const [onlySel, setOnlySel] = useState(initialSelected.length > 0);
  const [q, setQ] = useState('');
  const [type, setType] = useState<'all' | 'photo' | 'video'>(initialType);
  const [sort, setSort] = useState<Sort>('recent');
  const [resyncing, setResyncing] = useState(false);
  const [view, setView] = useState<{ items: CatalogueItem[]; start: number } | null>(null);
  const excluded = useMemo(() => new Set(excludeIds ?? []), [excludeIds]);

  // Esc cancels (or just leaves the full-screen view).
  useEffect(() => {
    const k = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); if (view !== null) setView(null); else onClose(); } };
    window.addEventListener('keydown', k, true);
    return () => window.removeEventListener('keydown', k, true);
  }, [onClose, view]);

  // Pick a vault once the list arrives — the one holding the first picked item
  // when known, else the first. Only fetched once it's actually on screen.
  useEffect(() => {
    if (!cat.vaults.length) return;
    const has = (id?: string) => !!id && cat.vaults.some((x) => x.id === id);
    const first = initialSelected.map((id) => vaultHints?.[id]).find(has);
    setVault((v) => (has(v) ? v : first ?? cat.vaults[0]!.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cat.vaults]);
  useEffect(() => { if (vault && !onlySel) cat.loadVault(vault); }, [cat, vault, onlySel]);

  // Already-picked ids may live in vaults we haven't browsed: load them so the
  // tray and the "Selected" view can show their thumbnails.
  useResolveIds(accountId, cat, initialSelected, vaultHints);

  const byVault = useMemo(() => {
    const m = new Map<string, CatalogueItem[]>();
    for (const it of cat.items) {
      if (excluded.has(it.id)) continue;
      const a = m.get(it.vault_id) ?? [];
      a.push(it);
      m.set(it.vault_id, a);
    }
    return m;
  }, [cat.items, excluded]);

  const selItems = sel.map((id) => cat.byId.get(id)).filter((x): x is CatalogueItem => !!x);
  // Picked ids whose vault hasn't arrived yet — placeholders in the Selected view.
  const selPending = onlySel && type === 'all' ? sel.length - selItems.length : 0;
  const base = onlySel ? selItems : byVault.get(vault) ?? [];
  const nV = base.filter((i) => isVideoItem(i.type)).length;
  const nP = base.length - nV;
  let list = base.filter((i) => type === 'all' || (type === 'video') === isVideoItem(i.type));
  if (!onlySel) {
    const ts = (i: CatalogueItem) => (i.created_at ? Date.parse(i.created_at) : 0);
    if (sort === 'recent') list = [...list].sort((a, b) => ts(b) - ts(a));
    if (sort === 'oldest') list = [...list].sort((a, b) => ts(a) - ts(b));
    if (sort === 'longest') list = [...list].sort((a, b) => (isVideoItem(b.type) ? b.duration ?? 0 : 0) - (isVideoItem(a.type) ? a.duration ?? 0 : 0));
  }
  const ids = list.map((i) => i.id);
  const allOn = ids.length > 0 && ids.every((id) => sel.includes(id));
  const toggle = (id: string) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const selectAll = () => setSel((s) => (allOn ? s.filter((x) => !ids.includes(x)) : [...s, ...ids.filter((x) => !s.includes(x))]));

  const vids = selItems.filter((i) => isVideoItem(i.type));
  const photos = sel.length - vids.length;
  const secs = vids.reduce((a, i) => a + (i.duration ?? 0), 0);
  const mix = [photos ? t('picker.photos', { count: photos }) : '', vids.length ? t('picker.videos', { count: vids.length }) : ''].filter(Boolean).join(' · ');
  const summary = sel.length ? [t('picker.selected', { count: sel.length }), mix, secs ? t('picker.ofVideo', { len: fmtDuration(secs) }) : ''].filter(Boolean).join(' · ') : t('picker.nothing');
  const hint = fit?.(photos, vids.length) ?? null;
  const canConfirm = sel.length > 0 || !!allowEmpty;
  const qq = q.trim().toLowerCase();
  const vaultRows = cat.vaults.filter((v) => !qq || v.vault_name.toLowerCase().includes(qq));
  const curName = onlySel ? t('picker.selectedView') : cat.vaults.find((v) => v.id === vault)?.vault_name ?? '';

  return createPortal(
    <div className="cpt-root" style={{ position: 'fixed', inset: 0, zIndex: 1200, background: 'color-mix(in srgb, var(--om-page) 72%, transparent)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ width: '100%', maxWidth: 1120, height: '100%', maxHeight: 780, display: 'flex', flexDirection: 'column', background: C.surface, border: `1px solid ${C.borderHi}`, borderRadius: 12, overflow: 'hidden', boxShadow: '0 24px 64px rgba(0,0,0,.5)' }}>
        <div style={{ height: 48, flex: 'none', display: 'flex', alignItems: 'center', gap: 10, padding: '0 10px 0 16px', borderBottom: `1px solid ${C.divider}` }}>
          <Icon name="layers" size={15} color={C.purpleLt} />
          <span style={{ font: font(700, 15), color: C.text, whiteSpace: 'nowrap' }}>{title}</span>
          <span style={{ font: font(400, 12), color: C.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t('picker.sub')}</span>
          <span style={{ flex: 1 }} />
          <button type="button" onClick={onClose} title={t('picker.close')} style={{ width: 30, height: 30, border: 0, borderRadius: 6, background: 'transparent', color: C.sub, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name="x" size={16} /></button>
        </div>
        <div className="cpt-layout" style={{ flex: 1, minHeight: 0, display: 'flex' }}>
          <aside style={{ width: 208, flex: 'none', borderRight: `1px solid ${C.divider}`, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <div style={{ flex: 'none', padding: '10px 10px 6px', position: 'relative' }}>
              <Icon name="search" size={13} color={C.muted} style={{ position: 'absolute', left: 18, top: 17 }} />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('picker.find')} style={{ width: '100%', height: 28, boxSizing: 'border-box', background: C.input, border: `1px solid ${C.border}`, borderRadius: 6, padding: '0 8px 0 27px', color: C.text, font: font(400, 12.5), outline: 'none' }} />
            </div>
            <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '0 6px 10px', display: 'flex', flexDirection: 'column', gap: 1 }}>
              <button type="button" onClick={() => setOnlySel(true)} style={{ height: 30, flex: 'none', display: 'flex', alignItems: 'center', gap: 8, padding: '0 8px', border: 0, borderRadius: 6, background: onlySel ? C.tint16 : 'transparent', color: onlySel ? C.text : C.sub, font: font(500, 12.5), cursor: 'pointer', textAlign: 'left' }}>
                <Icon name="check-circle" size={13} />
                <span style={{ flex: 1 }}>{t('picker.selectedView')}</span>
                <span style={{ minWidth: 18, padding: '0 5px', height: 17, boxSizing: 'border-box', borderRadius: 99, background: sel.length ? C.purple : C.hover, color: sel.length ? '#fff' : C.muted, font: font(600, 10.5, 17 / 10.5), textAlign: 'center' }}>{sel.length}</span>
              </button>
              <div style={{ padding: '12px 8px 4px', font: font(600, 10), letterSpacing: '.06em', textTransform: 'uppercase', color: C.muted }}>{t('picker.vaults')}</div>
              {vaultRows.map((v) => {
                const a = !onlySel && v.id === vault;
                const loaded = byVault.get(v.id);
                const picked = loaded ? loaded.filter((i) => sel.includes(i.id)).length : 0;
                return (
                  <button key={v.id} type="button" onClick={() => { setVault(v.id); setOnlySel(false); }} className={a ? undefined : 'cpt-hover-row'} style={{ height: 30, flex: 'none', width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '0 8px', border: 0, borderRadius: 6, background: a ? C.tint16 : 'transparent', color: a ? C.text : C.sub, font: font(500, 12.5), cursor: 'pointer', textAlign: 'left' }}>
                    <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.vault_name}</span>
                    {picked > 0 && <span style={{ minWidth: 18, padding: '0 5px', height: 17, boxSizing: 'border-box', borderRadius: 99, background: C.purple, color: '#fff', font: font(600, 10.5, 17 / 10.5), textAlign: 'center' }}>{picked}</span>}
                    {loaded && <span style={{ font: font(400, 11.5), color: C.muted }}>{loaded.length}</span>}
                  </button>
                );
              })}
              {!cat.loading && !cat.vaults.length && <div style={{ padding: 12, font: font(400, 12), color: C.muted }}>{t('picker.noVaults')}</div>}
            </div>
          </aside>
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
            <div style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', flexWrap: 'wrap' }}>
              <span style={{ font: font(600, 13), color: C.text, whiteSpace: 'nowrap' }}>{curName}</span>
              <Seg value={type} onChange={setType} options={[
                { id: 'all', label: t('picker.all'), n: nP + nV },
                { id: 'photo', label: t('picker.photosTab'), n: nP },
                { id: 'video', label: t('picker.videosTab'), n: nV },
              ]} />
              <Btn kind="text" onClick={selectAll} disabled={!ids.length}>{allOn ? t('picker.deselectAll') : t('picker.selectAll', { count: ids.length })}</Btn>
              <span style={{ flex: 1 }} />
              {cat.loading && <Spin size={13} />}
              {!onlySel && (
                <select value={sort} onChange={(e) => setSort(e.target.value as Sort)} style={{ height: 28, background: C.input, border: `1px solid ${C.border}`, borderRadius: 6, padding: '0 8px', color: C.text, font: font(500, 12), outline: 'none' }}>
                  <option value="recent">{t('picker.sortRecent')}</option>
                  <option value="oldest">{t('picker.sortOldest')}</option>
                  <option value="longest">{t('picker.sortLongest')}</option>
                </select>
              )}
              <button type="button" title={t('picker.refresh')} disabled={resyncing} onClick={() => { setResyncing(true); void cat.resync().finally(() => setResyncing(false)); }} style={{ width: 28, height: 28, background: 'transparent', border: `1px solid ${C.borderHi}`, borderRadius: 6, color: C.sub, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {resyncing ? <Spin size={13} /> : <Icon name="refresh-cw" size={13} />}
              </button>
            </div>
            <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '0 16px 16px' }}>
              {cat.error && <div style={{ padding: '0 0 10px', font: font(400, 12), color: C.purplePale }}>{cat.error}</div>}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(112px,1fr))', gap: 8 }}>
                {list.map((it) => {
                  const n = sel.indexOf(it.id) + 1;
                  const vid = isVideoItem(it.type);
                  return (
                    <div key={it.id} onClick={() => toggle(it.id)} style={{ position: 'relative', aspectRatio: '1', borderRadius: 8, overflow: 'hidden', cursor: 'pointer', background: C.stripe }}>
                      <VaultThumb accountId={accountId} url={thumbUrl(it)} className="w-full h-full" />
                      <div style={{ position: 'absolute', inset: 0, borderRadius: 8, boxShadow: `inset 0 0 0 2px ${n ? C.purple : 'transparent'}`, background: n ? 'color-mix(in srgb, var(--om-accent) 14%, transparent)' : 'transparent', pointerEvents: 'none' }} />
                      <span style={{ position: 'absolute', top: 6, right: 6, width: 20, height: 20, boxSizing: 'border-box', borderRadius: 99, border: `1.5px solid ${n ? C.purple : 'color-mix(in srgb, var(--om-text) 75%, transparent)'}`, background: n ? C.purple : 'color-mix(in srgb, var(--om-page) 45%, transparent)', display: 'flex', alignItems: 'center', justifyContent: 'center', font: font(700, 10.5), color: '#fff' }}>{n || ''}</span>
                      {vid && <span style={{ position: 'absolute', left: 6, bottom: 6, padding: '2px 6px', borderRadius: 4, background: 'color-mix(in srgb, var(--om-page) 80%, transparent)', display: 'flex', alignItems: 'center', gap: 4, font: font(500, 10.5), color: C.text, whiteSpace: 'nowrap' }}><Icon name="play" size={9} />{fmtDuration(it.duration)}</span>}
                      <button type="button" title={t('picker.fullScreen')} onClick={(e) => { e.stopPropagation(); setView({ items: list, start: list.indexOf(it) }); }} className="cpt-hover-text" style={{ position: 'absolute', right: 6, bottom: 6, width: 22, height: 22, border: 0, padding: 0, borderRadius: 5, background: 'color-mix(in srgb, var(--om-page) 65%, transparent)', color: 'color-mix(in srgb, var(--om-text) 80%, transparent)', cursor: 'zoom-in', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name="maximize-2" size={11} /></button>
                      {onlySel && <span style={{ position: 'absolute', left: 6, top: 6, maxWidth: 'calc(100% - 40px)', overflow: 'hidden', textOverflow: 'ellipsis', padding: '2px 6px', borderRadius: 4, background: 'color-mix(in srgb, var(--om-page) 80%, transparent)', font: font(500, 10.5), color: C.purplePale, whiteSpace: 'nowrap' }}>{it.vault_name}</span>}
                    </div>
                  );
                })}
                {Array.from({ length: selPending }, (_, j) => (
                  <div key={`pending-${j}`} className={cat.loading ? 'animate-pulse' : undefined} style={{ aspectRatio: '1', borderRadius: 8, background: C.stripe, boxShadow: `inset 0 0 0 1px ${C.borderHi}` }} />
                ))}
              </div>
              {!list.length && !selPending && !(cat.loading && !onlySel) && (
                <div style={{ padding: 36, textAlign: 'center', border: `1px dashed ${C.border}`, borderRadius: 8, font: font(400, 12.5), color: C.muted }}>
                  {onlySel ? t('picker.noneSelected') : t('picker.noneHere')}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="cpt-row" style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 14, padding: '10px 16px', borderTop: `1px solid ${C.divider}`, minHeight: 64, boxSizing: 'border-box' }}>
          <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 12 }}>
            {sel.length > 0 && (
              <div style={{ display: 'flex', gap: 4, overflowX: 'auto', maxWidth: '50%', flex: 'none', padding: '2px 0' }}>
                {sel.map((id, j) => {
                  const it = cat.byId.get(id);
                  const vid = it ? isVideoItem(it.type) : false;
                  return (
                    <button key={id} type="button" onClick={() => toggle(id)} title={t('picker.remove')} style={{ position: 'relative', width: 40, height: 40, flex: 'none', border: 0, padding: 0, borderRadius: 6, cursor: 'pointer', background: C.stripe, overflow: 'hidden', boxShadow: `inset 0 0 0 1px ${C.borderHi}` }}>
                      {it && <VaultThumb accountId={accountId} url={thumbUrl(it)} className="w-full h-full" />}
                      <span style={{ position: 'absolute', top: 2, left: 3, font: font(700, 9.5), color: C.purplePale, textShadow: '0 1px 2px #000' }}>{j + 1}</span>
                      {vid && <span style={{ position: 'absolute', bottom: 3, left: '50%', transform: 'translateX(-50%)', padding: '0 3px', borderRadius: 3, background: 'color-mix(in srgb, var(--om-page) 80%, transparent)', font: font(600, 9), color: C.text, whiteSpace: 'nowrap' }}>{fmtDuration(it?.duration)}</span>}
                    </button>
                  );
                })}
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
              <span style={{ font: font(500, 12.5), color: C.text, whiteSpace: 'nowrap' }}>{summary}</span>
              {hint && <span style={{ font: font(400, 11.5), color: hint.tone === 'warn' ? C.purplePale : C.sub, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{hint.text}</span>}
            </div>
          </div>
          {sel.length > 0 && <Btn kind="text" onClick={() => setSel([])} style={{ color: C.sub }}>{t('picker.clear')}</Btn>}
          <Btn kind="ghost" height={30} onClick={onClose}>{t('picker.cancel')}</Btn>
          <Btn kind="primary" height={30} disabled={!canConfirm} onClick={() => canConfirm && onConfirm(sel, selItems)}>
            {sel.length ? t('picker.add', { count: sel.length }) : allowEmpty ? t('picker.save') : t('picker.addItems')}
          </Btn>
        </div>
      </div>
      {view && <MediaLightbox accountId={accountId} items={view.items} start={view.start} onClose={() => setView(null)} />}
    </div>,
    document.body,
  );
}
